import React, { useState } from 'react';
import { Table, message, Modal } from 'antd';
import s from './s.module.less';
import useGetMyChannels from 'hooks/useGetMyChannels';
import { Channel, EListStatus } from 'types/common';
import { unListPPAChannel, reactivatePremiumChannel, removePremiumChannel } from 'api/channel';
import { openChannelSubscriptionModal } from 'utils/globalLayerControl';
import { Email } from 'constants/contact';
import dayjs from 'dayjs';
import ChannelStatus from 'pages/dashboard/ChannelMainPage/components/ActiveChannelStatus';
import RemovalConfirm from 'pages/dashboard/ChannelMainPage/components/MyChannel/components/RemovalConfirm';
import RemoveSuccessfully from 'pages/dashboard/ChannelMainPage/components/MyChannel/components/RemoveSuccessfully';
import ReactiveSuccessfully from 'pages/dashboard/ChannelMainPage/components/MyChannel/components/ReactiveSuccessfully';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { isKlarityHost } from 'utils/common';

interface IProps {
    ppaChannelId: number;
}

const StandardListingTable = ({ ppaChannelId }: IProps) => {
    const [myChannels, loading, error, refetch] = useGetMyChannels();
    const [setting] = useSettings();
    const listStatusOptions = setting[EnumFields.LIST_CHANNEL_STATUS];

    const [showRemovalConfirm, setShowRemovalConfirm] = useState(false);
    const [removeChannelId, setRemoveChannelId] = useState<number | null>(null);
    const [reactiveChannel, setReactiveChannel] = useState<Channel | null>(null);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [reactiveLoading, setReactiveLoading] = useState(false);
    const [showReactiveSuccessfully, setShowReactiveSuccessfully] = useState(false);
    const [showRemoveSuccessfully, setShowRemoveSuccessfully] = useState(false);
    // 记录被删除的Channel，用来获取billingCycleEndDate
    const [removedChannel, setRemoveChannel] = useState<Channel>();

    const channel = myChannels?.premiumList?.find((c: Channel) => c.id === ppaChannelId);

    if (!channel) {
        return null;
    }

    const handleUnlistPremiumChannel = async () => {
        const res = await unListPPAChannel();
        if (res && !res.error) {
            message.success('Unlisted successfully');
            refetch();
        } else {
            message.error(res.error);
        }
    };

    const handleReactivePremiumChannel = async (channelId: number | null) => {
        if (channelId) {
            setReactiveLoading(true);
            try {
                const res = await reactivatePremiumChannel(channelId);

                if (res && !res.error) {
                    setShowReactiveSuccessfully(true);
                } else {
                    message.error(res.error);
                }
            } catch (e) {
                console.error(e);
            }
            setReactiveLoading(false);
        }
    };

    const getRemovedChannel = (channelId: number) => {
        if (channelId) {
            const removedPremiumChannel = myChannels?.premiumList?.find((c: Channel) => c.id === channelId);

            if (removedPremiumChannel) {
                setRemoveChannel(removedPremiumChannel);
            }
        }
    };

    const handleRemovalConfirmConfirm = async () => {
        if (removeChannelId) {
            setRemoveLoading(true);
            try {
                const res = await removePremiumChannel(removeChannelId);

                if (res && !res.error) {
                    setShowRemovalConfirm(false);
                    setShowRemoveSuccessfully(true);
                    getRemovedChannel(removeChannelId);
                } else {
                    message.error(res.error);
                }
            } catch (e) {
                console.error(e);
            }
            setRemoveLoading(false);
        }
    };

    const premiumTableActionRender = (row: Channel) => {
        const { payStatus, listStatus, removeStatus, ppaChannel } = row;

        if (payStatus === 'failed' && !ppaChannel) {
            return <a className={s.actionCell} onClick={() => openChannelSubscriptionModal({ channel: row })}>Update payment</a>;
        }
        if (payStatus === 'failed' && ppaChannel) {
            return null;
        }

        if (payStatus === 'paid') {
            // 优先判断removeStatus，如果是pending，说明已经被删除，在处理中
            if (removeStatus === 'Pending') {
                return (
                    <a
                        className={s.actionCell}
                        onClick={() => {
                            setReactiveChannel(row);
                            handleReactivePremiumChannel(row.id);
                        }}
                    >Reactivate
                    </a>
                );
            }

            if (ppaChannel) {
                if (listStatus === EListStatus.LISTED) {
                    // return (
                    //     <div
                    //         className={s.actionCell}
                    //         onClick={() => {
                    //             handleUnlistPremiumChannel();
                    //         }}
                    //     >
                    //         Un-list
                    //     </div>
                    // );
                    return null;
                }
                const email = isKlarityHost() ? Email.klarity_provider_support : Email.support;

                if (listStatus === EListStatus.UNDER_REVIEW || listStatus === EListStatus.REJECTED) {
                    
                    return (
                        <a className={s.actionCell} href={`mailto:${email}`}>
                            Follow up
                        </a>
                    );
                }

                if (listStatus === EListStatus.NOT_LISTED) {
                    return (
                        <div
                            className={s.actionCell}
                            onClick={() => {
                                window.open(`mailto:${email}`);
                            }}
                        >
                            Follow up with Support team
                        </div>
                    );
                }

                return '-';
            }

            if (listStatus === 'under-review' || listStatus === 'rejected') {
                return <a className={s.actionCell} href={`mailto:${Email.support}`}>Follow up</a>;
            }

            if (listStatus === 'listed') {
                return (
                    <div
                        className={s.actionCell}
                        onClick={() => {
                            setRemoveChannelId(row.id);
                            setShowRemovalConfirm(true);
                        }}
                    >
                        Remove
                    </div>);
            }
        }
    };

    const getStatusText = (listStatus: string) => {
        const statusItem = listStatusOptions?.find((option) => listStatus === option.value);

        return statusItem?.label;
    };

    const standardChannelColumn =
        [
            {
                title: 'CHANNEL NAME',
                dataIndex: 'name',
                key: 'name',
                render: (name: string, row: Channel) => {
                    return (
                        <div className={s.channelNameColumn}>
                            <img src={row.logo} />
                            {name}
                        </div>
                    );
                },
            },
            {
                title: 'STATUS',
                dataIndex: 'listStatus',
                key: 'listStatus',
                render: (listStatus: string, row: Channel) => {
                    const { removeStatus } = row;
                    return removeStatus === 'Pending' ? <div className={s.pendingRemoved}>Pending removed</div> : <ChannelStatus showDot status={listStatus} statusText={getStatusText(listStatus)} />;
                },
            },
            {
                title: 'PROFILE PAGE',
                dataIndex: 'profileUrl',
                key: 'profileUrl',
                render: (url: string, row: Channel) => {
                    if (row.listStatus !== 'listed') {
                        return 'Not listed yet';
                    }

                    if (url) {
                        let link = url;
                        if (url?.indexOf('https://') !== 0) {
                            link = `https://${url}`;
                        }
                        return <a target="_blank" className={s.link} href={link} rel="noreferrer">{link}</a>;
                    }

                    return null;
                },
            },
            {
                title: '',
                className: s.tableColumn,
                editable: false,
                render: (item: Channel) => {
                    return premiumTableActionRender(item);
                },
            },
        ];

    return (
        <div className={s.wrap}>
            <Table
                columns={standardChannelColumn}
                className={s.channelTable}
                dataSource={[channel]}
                pagination={
                    { hideOnSinglePage: true }
                }
            />
            <Modal
                width="50%"
                footer={null}
                open={showRemovalConfirm}
                onCancel={() => setShowRemovalConfirm(false)}
            >
                <RemovalConfirm
                    loading={removeLoading}
                    onCancel={() => setShowRemovalConfirm(false)}
                    onConfirm={handleRemovalConfirmConfirm}
                />
            </Modal>
            <Modal
                width="45%"
                footer={null}
                open={showRemoveSuccessfully}
                onCancel={() => setShowRemoveSuccessfully(false)}
            >
                <RemoveSuccessfully
                    removedChannel={removedChannel}
                    onClose={() => setShowRemoveSuccessfully(false)}
                />
            </Modal>
            <Modal
                width="45%"
                footer={null}
                open={showReactiveSuccessfully}
                onCancel={() => setShowReactiveSuccessfully(false)}
            >
                <ReactiveSuccessfully
                    nextBillingDate={reactiveChannel ? reactiveChannel.nextPaymentDue : ''}
                    onClose={() => setShowReactiveSuccessfully(false)}
                />
            </Modal>
        </div>
    );
};

export default StandardListingTable;
